import React, { useState } from "react";
import { Copy, Download, Share2, Send, RefreshCw, Pencil, Trash2, CheckCheck, Loader2, Film } from "lucide-react";
import { BACKEND_URL, fullCaption } from "../../lib/studioApi";

const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-black uppercase tracking-widest border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const ghost = `${btn} border-zinc-800 text-zinc-300 hover:border-[#BEFF38] hover:text-white`;
const primary = `${btn} bg-[#BEFF38] border-[#BEFF38] text-black hover:bg-[#a8e62e]`;

const fetchFiles = async (post, format) => {
  if (format === "reel") {
    const blob = await (await fetch(`${BACKEND_URL}${post.reel_url}`)).blob();
    return [new File([blob], `freefitfuel-${post.id.slice(0, 6)}-reel.mp4`, { type: "video/mp4" })];
  }
  let urls = format === "story" ? [post.story_video_url || post.story_url] : format === "niggle" ? post.frame_video_urls || post.slide_urls : [...(post.slide_urls || [post.feed_url])];
  if (format === "carousel" && post.carousel_video_url) urls[0] = post.carousel_video_url;
  return Promise.all(urls.map(async (u, i) => {
    const blob = await (await fetch(`${BACKEND_URL}${u}`)).blob();
    const name = format === "story" ? "story" : `${format === "niggle" ? "frame" : "slide"}-${i + 1}`;
    const mp4 = u.includes(".mp4");
    return new File([blob], `freefitfuel-${post.id.slice(0, 6)}-${name}.${mp4 ? "mp4" : "jpg"}`, { type: mp4 ? "video/mp4" : "image/jpeg" });
  }));
};

const PUB_LABEL = { publishing: "Publishing…", published: "Published", failed: "Retry publish" };

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const PostActions = ({ post, format, igConnected, notify, onEdit, onRegen, onDelete, onPublish, onTogglePosted, onReel }) => {
  const [busy, setBusy] = useState("");
  const isReel = format === "reel";
  const ready = post.image_status !== "generating" && (!isReel || post.reel_status === "ready");
  const wrap = (k, fn) => async () => { setBusy(k); try { await fn(); } finally { setBusy(""); } };

  const copy = wrap("copy", async () => {
    await navigator.clipboard.writeText(fullCaption(post));
    notify("Caption + hashtags copied");
  });
  const download = wrap("dl", async () => {
    const files = await fetchFiles(post, format);
    for (const file of files) {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file);
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(a.href);
      await sleep(350);
    }
    notify(format === "niggle" ? "4 story frames downloaded — post them in order" : files.length > 1 ? `${files.length} slides downloaded — post them as one carousel` : `${isReel ? "Reel" : "Story"} downloaded`);
  });
  const share = wrap("share", async () => {
    const files = await fetchFiles(post, format);
    await navigator.clipboard.writeText(fullCaption(post)).catch(() => {});
    if (navigator.canShare && navigator.canShare({ files })) {
      notify("Caption copied — paste it into Instagram");
      await navigator.share({ files, text: fullCaption(post) }).catch(() => {});
    } else {
      notify("Sharing files isn't supported here — use Download instead");
    }
  });
  const posted = post.status === "posted";
  const pub = post.ig?.[`${format}_status`];
  const pubErr = pub === "failed" ? post.ig?.[`${format}_error`] : "";
  const dlLabel = isReel ? "Download reel" : format === "story" ? "Download story"
    : format === "niggle" ? `Download ${post.slide_urls?.length || 4} frames` : `Download ${post.slide_urls?.length || 1} slides`;

  return (
    <div className="flex flex-col gap-2">
    <div className="flex flex-wrap gap-2">
      <button className={primary} onClick={copy} disabled={!!busy} data-testid={`post-copy-${post.id}`}><Copy className="w-3.5 h-3.5" /> Copy caption</button>
      <button className={ghost} onClick={download} disabled={!ready || !!busy} data-testid={`post-download-${post.id}`}><Download className="w-3.5 h-3.5" /> {dlLabel}</button>
      <button className={ghost} onClick={share} disabled={!ready || !!busy} data-testid={`post-share-${post.id}`}><Share2 className="w-3.5 h-3.5" /> Share</button>
      <button
        className={ghost}
        onClick={wrap("pub", () => onPublish(format))}
        disabled={!igConnected || !ready || !!busy || pub === "published" || pub === "publishing"}
        title={igConnected ? "" : "Connect Instagram to enable auto-publish"}
        data-testid={`post-publish-${post.id}`}
      >
        {busy === "pub" || pub === "publishing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        {PUB_LABEL[pub] || (format === "niggle" ? "Publish story set" : `Publish ${format}`)}
      </button>
      {isReel && post.reel_status === "ready" && (
        <button className={ghost} onClick={wrap("reel", onReel)} disabled={!!busy} data-testid={`post-rebuild-reel-${post.id}`}><Film className="w-3.5 h-3.5" /> Rebuild reel</button>
      )}
      <button className={ghost} onClick={wrap("posted", onTogglePosted)} disabled={!!busy} data-testid={`post-mark-posted-${post.id}`}>
        <CheckCheck className="w-3.5 h-3.5" /> {posted ? "Mark draft" : "Mark posted"}
      </button>
      <button className={ghost} onClick={onEdit} disabled={!!busy} data-testid={`post-edit-${post.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
      <button className={ghost} onClick={wrap("regen", onRegen)} disabled={!ready || !!busy} data-testid={`post-regen-${post.id}`}><RefreshCw className="w-3.5 h-3.5" /> New photo</button>
      <button className={`${ghost} hover:!border-red-500 hover:!text-red-400`} onClick={wrap("del", onDelete)} disabled={!!busy} data-testid={`post-delete-${post.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
    {pubErr && <p data-testid={`post-publish-error-${post.id}`} className="text-xs text-red-400">Instagram: {pubErr}</p>}
    </div>
  );
};
